<template>
  <div id="dashboard-wrapper" class="container-fluid">
    <widget-expander :dateRange="dateRange" :selected-account="selectedAccount"/>
    <drawer :show="campaignAnalyzer.show"
            no-scrim
            right
            card-body-classes="pt-0"
            @update:show="campaignAnalyzer.show = $event"
            @close="hideCampaignAnalyzer()">
      <template #title>
        Analyze Campaign: {{ campaignAnalyzer.campaignBeingAnalyzed }}
      </template>
      <analyze-campaign v-if="campaignAnalyzer.show"
                        :campaign-plan-prop="campaignAnalyzer.campaignProp"
                        @campaign-loaded="setCampaignToAnalyze($event)" />
    </drawer>
    <div class="d-flex align-items-center">
      <div class="flex-grow-1" style="margin-top: 7px;">
        <template v-if="selectedAccount">
          <h2 class="mt-2 d-inline">Dashboard for {{selectedAccountName}}</h2>
          <t-button-inline class="d-inline ml-2" size="small" @click="selectedAccount = null">see all accounts</t-button-inline>
        </template>
        <h2 v-else class="mt-2">Dashboard</h2>
      </div>

      <div v-if="!selectedAccount" class="py-3 px-4">
        <small class="text-muted d-block">View By Segment</small>
        <select-available-segments size="sm"></select-available-segments>
      </div>

      <div class="py-3 px-4">
        <small class="text-muted d-block dashboard-account-selector">View By Account</small>
        <dashboard-account-selector v-model="selectedAccount" style="min-width: 200px"></dashboard-account-selector>
      </div>

      <div class="py-3 px-4">
        <small class="text-muted d-block">Select a Month</small>
        <dashboard-date-selector v-model="dateRange" class="dashboard-filter-date"></dashboard-date-selector>
      </div>

      <div class="py-3 pl-4 d-flex-end-start flex-column">
        <small class="text-muted d-block ml-3">Dashboard Layout</small>
        <t-button :variant="(this.editing) ? 'warning' : 'primary'"
               class="py-2"
               @click="toggleEditMode()">{{(this.editing) ? 'Save' : 'Edit'}} Layout</t-button>
      </div>

    </div>

    <hr class="my-0"/>
    <div class="row justify-content-center">
      <div class="col-12 dashboard-container">
        <div class="dashboard-container-header py-2">
          <top-metrics :date-range="dateRange" :selected-account="selectedAccount"/>
        </div>
        <div class="row">
          <div class="col-12 p-0">
            <div v-if="editing"
                 class="sticky-top"
                 :style="{paddingTop: (hasScrolledPastHeader) ? `${headerHeight}px` : 0}">
              <div class="border rounded mt-4 mb-0 mx-3 py-4 px-5 new-widget-adder alert alert-warning">
<!--                <div class="form-inline">-->
<!--                  <label for="dashboard-choose-new-widget">Add a Widget to your Dashboard</label>-->
<!--                  <select id="dashboard-choose-new-widget"-->
<!--                          name="dashboard-choose-new-widget"-->
<!--                          class="form-control mr-3"-->
<!--                          ref="newWidgetSelector">-->
<!--                    <option value="-1">-&#45;&#45; Choose Widget -&#45;&#45;</option>-->
<!--                    <option v-for="(widget, widgetKey) in widgets"-->
<!--                            :value="widget.widgetRegistryId"-->
<!--                            :key="widgetKey">{{widget.name}}</option>-->
<!--                  </select>-->
<!--                  <button type="submit"-->
<!--                          class="btn btn-primary"-->
<!--                          @click="addWidget()">Add Widget</button>-->
<!--                </div>-->
                <t-dropdown text="Add a Widget to your Dashboard" severity="contrast" menu-class="scroll-limit">
                  <t-dropdown-text>Fluency Widgets</t-dropdown-text>
                  <t-dropdown-item v-for="(widget, widgetKey) in widgets" :key="widgetKey" @click="addWidget(widget.widgetRegistryId)">{{widget.name}}</t-dropdown-item>
                  <template v-if="dash && datasets.OWNED && datasets.OWNED.length > 0">
                    <t-dropdown-text>Your Data Sets</t-dropdown-text>
                    <t-dropdown-item v-for="item in datasets.OWNED" :key="item.name" @click="addWidget(undefined, item.reportDatasetId)">{{item.name}}</t-dropdown-item>
                  </template>
                  <template v-if="dash && datasets.SHARED && datasets.SHARED.length > 0">
                    <t-dropdown-text>Data Sets Shared with You</t-dropdown-text>
                    <t-dropdown-item v-for="item in datasets.SHARED" :key="item.name" @click="addWidget(undefined, item.reportDatasetId)">{{item.name}}</t-dropdown-item>
                  </template>
                  <template v-if="dash && datasets.PUBLIC && datasets.PUBLIC.length > 0">
                    <t-dropdown-text>Global Data Sources</t-dropdown-text>
                    <t-dropdown-item v-for="item in datasets.PUBLIC" :key="item.name" @click="addWidget(undefined, item.reportDatasetId)">{{item.name}}</t-dropdown-item>
                  </template>
                </t-dropdown>
              </div>
            </div>
            <grid-layout v-if="displayWidgets.length && dataLoaded"
                         ref="grid"
                         :layout="displayWidgets"
                         :margin="[grid.margin.x, grid.margin.y]"
                         :col-num="grid.numCols"
                         :row-height="grid.rowHeight"
                         :is-draggable="editing"
                         :is-resizable="editing"
                         :is-mirrored="false"
                         :vertical-compact="true"
                         :use-css-transforms="false"
                         @layout-updated="layoutUpdatedEvent">
              <grid-item v-for="(item, displayWidgetIndex) in displayWidgets"
                         :key="`${displayWidgetIndex}${item.url}`"
                         :x="item.x"
                         :y="item.y"
                         :w="item.w"
                         :h="item.h"
                         :i="item.i"
                         :maxW="grid.item.maxW"
                         :maxH="grid.item.maxH"
                         class="touch-action-none"
                         dragAllowFrom=".dragger">
                <div v-if="editing" class="position-absolute h-100 w-100 widget-editing-overlay p-4">
                  <span class="dragger float-right">
                    <fluency-icon type="move" size="lg" />
                  </span>
                  <span class="trash-widget float-right mr-4">
                    <t-button variant="link"
                           class="m-0 p-0 text-dark"
                           @click="deleteWidget(displayWidgetIndex)">
                      <fluency-icon type="delete" size="lg" />
                    </t-button>
                  </span>
                  <h4>{{item.name}}</h4>
                </div>

                <div v-if="item.source === 'BACKPACK' && dynamicComponents[item.name]"
                          class="h-100">
                  <component :item="item"
                             :itemKey="displayWidgetIndex"
                             :editing="editing"
                             :isNewWidget="isNewWidget(displayWidgetIndex)"
                             :selectedAccount="selectedAccount"
                             :dateRange="dateRange"
                             :is="dynamicComponents[item.name]" />
                </div>
                <div v-else-if="item.source === 'DATA_EXPLORER'" class="h-100">
                  <dashboard-dataset :item="item"
                                     :itemKey="displayWidgetIndex"
                                     :editing="editing"
                                     :isNewWidget="isNewWidget(displayWidgetIndex)"
                                     :selectedAccount="selectedAccount"
                                     :dateRange="dateRange"></dashboard-dataset>
                </div>
                <div :id="`dashboard-widget-${displayWidgetIndex}`"></div>
              </grid-item>
            </grid-layout>

            <div :style="`height: ${Math.max(grid.rowHeight, maxHeightInPixels)}px`"><!-- spacer so that grid-layout has room to drag into --></div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserGuardRails from '@/components/pages/dashboard/userGuardRails'
import _ from 'lodash'
import GridLayout from 'core-ui/components/grid/components/grid-layout.vue'
import GridItem from 'core-ui/components/grid/components/grid-item.vue'
import 'core-ui/components/grid/gridStyle.scss'
import TopMetrics from '@/components/pages/dashboard/topMetrics'
import WidgetExpander from '@/components/pages/dashboard/widgetExpander'
import DashboardDateSelector from '@/components/pages/dashboard/dashboardDateSelector'
import DashboardAccountSelector from '@/components/pages/dashboard/dashboardAccountSelector'
import SelectAvailableSegments from '@/components/test/selectAvailableSegments'
import Drawer from 'core-ui/components/common/drawer.vue'
import DashboardDataset from 'core-ui/components/dashboard/dashboardDataset.vue'
import _throttle from 'lodash.throttle'
import AnalyzeCampaign from '@/components/pages/manage/campaign/analyzeCampaign.vue'

import MetricTrends from '@/components/pages/dashboard/metricTrends.vue'

import AccountEvents from 'core-ui/components/dashboard/accountEvents'
import BenchmarkGuardrailAnalysis from 'core-ui/components/dashboard/benchmarkGuardrailAnalysis'
import BudgetPacing from 'core-ui/components/dashboard/budgetPacing'
import Demographics from 'core-ui/components/dashboard/demographics'
import DeviceMetrics from 'core-ui/components/dashboard/deviceMetrics'
import GeoHeatmap from 'core-ui/components/dashboard/geoHeatmap'
import HourHeatmap from 'core-ui/components/dashboard/hourHeatmap'
import ImpressionShare from 'core-ui/components/dashboard/impressionShare'
import KeywordBubble from 'core-ui/components/dashboard/keywordBubble'
import KeywordQualityScore from 'core-ui/components/dashboard/keywordQualityScore'
import LearningMode from 'core-ui/components/dashboard/learningMode'
import RecentLaunches from 'core-ui/components/dashboard/recentLaunches'
import TopCreatives from 'core-ui/components/dashboard/topCreatives'

export default {
  name: 'Dashboard',
  components: {
    AnalyzeCampaign,
    Drawer,
    SelectAvailableSegments,
    DashboardAccountSelector,
    DashboardDateSelector,
    WidgetExpander,
    TopMetrics,
    UserGuardRails,
    GridLayout,
    GridItem,
    AccountEvents,
    BenchmarkGuardrailAnalysis,
    BudgetPacing,
    HourHeatmap,
    MetricTrends,
    KeywordBubble,
    ImpressionShare,
    RecentLaunches,
    LearningMode,
    KeywordQualityScore,
    Demographics,
    GeoHeatmap,
    TopCreatives,
    DeviceMetrics,
    DashboardDataset
  },
  data () {
    return {
      componentLabel: 'Dashboard Widgets',
      whatCanYouDo: 'add/delete/move/resize',
      modalId: 'se-dashboard-modal-edit',
      domains: {
        R: {
          url: 'http://ec2-52-15-171-17.us-east-2.compute.amazonaws.com:3838/fluency'
        }
      },
      grid: {
        map: [],
        numCols: 4,
        rowHeight: 250,
        margin: {
          x: 10,
          y: 10
        },
        item: {
          maxW: 4,
          maxH: 4
        },
        defaults: {
          w: 1,
          h: 2
        }
      },
      newWidgets: [],
      widgets: [],
      usersWidgets: [],
      displayWidgets: [],
      accessToken: '',
      editing: false,
      firstTimeUserMessaging: false,
      dynamicComponents: {},
      dateRange: [this.$moment().startOf('month'), this.$moment().endOf('month')],
      selectedAccount: null,
      selectedAccountName: null,
      oldLayout: null,
      campaignAnalyzer: {
        show: false,
        campaignBeingAnalyzed: '',
        campaignProp: undefined
      },
      dataLoaded: false,
      datasets: {}
    }
  },
  head () {
    return {
      title: 'Dashboard'
    }
  },
  mounted () {
    // if it is the first day of the month
    if (this.$moment().subtract(1, 'days').month() !== this.$moment().month()) {
      this.dateRange = [
        this.$moment().subtract(1, 'days').startOf('month'),
        this.$moment().subtract(1, 'days').endOf('month')
      ]
    }
  },
  watch: {
    selectedAccount: {
      handler (newVal) {
        // get the name from store
        this.setName(newVal)

        // update the route
        const accountPlanId = parseInt(this.$route.query?.accountPlanId)
        let newPath = null
        if (newVal && newVal !== accountPlanId) {
          newPath = `/dashboard/?accountPlanId=${newVal}`
        } else if (newVal === null) {
          newPath = '/dashboard/'
        }
        if (newPath && this.$route.fullPath !== newPath) {
          this.$router.replace(newPath)
        }
      },
      immediate: false
    },
    '$route.query.accountPlanId': {
      handler (val) {
        if (val) {
          const accountPlanId = parseInt(val)
          if (accountPlanId !== this.selectedAccount) {
            this.$setCompat(this, 'selectedAccount', accountPlanId)
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    setName (id) {
      const name = this.$store.getters.accountsById?.get(id)?.name
      this.$setCompat(this, 'selectedAccountName', name)
    },
    layoutUpdatedEvent: _throttle(async function (newLayout) {
      this.displayWidgets = newLayout

      if (this.oldLayout && !_.isEqual(this.oldLayout, this.displayWidgets)) {
        this.displayWidgets.forEach((value, index) => {
          this.usersWidgets[index].x = value.x
          this.usersWidgets[index].y = value.y
          this.usersWidgets[index].w = value.w
          this.usersWidgets[index].h = value.h
        })
        const response = await this.$res.set.dashboardWidgetsForUser(this.usersWidgets)

        if (!response) {
          this.$toast('Error while saving layout update', 'danger')
        }
        this.loadData()
      }

      this.$setCompat(this, 'oldLayout', _.cloneDeep(newLayout))
    }, 500),
    isNewWidget: function (i) {
      const index = this.newWidgets.indexOf(i)

      return index !== -1
    },
    async addWidget (widgetRegistryId, reportDatasetId) {
      if (widgetRegistryId || reportDatasetId) {
        const newPos = this.getNextGridPosition()
        const newObj = {
          widgetRegistryId,
          reportDatasetId,
          x: newPos.x,
          y: newPos.y,
          w: this.grid.defaults.w,
          h: this.grid.defaults.h
        }

        const newWidgetIndex = this.usersWidgets.push(newObj) - 1

        this.newWidgets.push(newWidgetIndex)

        const response = await this.$res.set.dashboardWidgetsForUser(this.usersWidgets)
        if (response) {
          this.$toast('Widget added', 'success')
        } else {
          this.$toast('Error adding widget', 'danger')
        }
        this.loadData()
        this.$nextTick(() => {
          this.$scrollTo(`#dashboard-widget-${newWidgetIndex}`, 500, {})
        })
      }
    },
    async deleteWidget (displayWidgetIndex) {
      const removedUserWidget = this.usersWidgets.splice(displayWidgetIndex, 1)
      if (removedUserWidget?.length === 1) {
        const displayWidget = this.displayWidgets.find(widg => widg.widgetRegistryId === removedUserWidget[0].widgetRegistryId)
        if (displayWidget?.name?.length > 0) {
          this.dynamicComponents[displayWidget.name] = false
        }
      }
      const response = await this.$res.set.dashboardWidgetsForUser(this.usersWidgets)
      if (response) {
        this.$toast('Widget removed', 'success')
      } else {
        this.$toast('Error removing widget', 'danger')
      }

      await this.loadData()
    },
    toggleEditMode: function (toggle) {
      this.$setCompat(this, 'editing', !this.editing)
      this.firstTimeUserMessaging = false

      if (!this.editing) {
        this.newWidgets = []
      }

      if (this.editing) {
        this.$res.set.trackUserEvent('edit-dashboard-layout', null)
      }
    },
    okClick: function () {
    },
    cancelClick: function () {
    },
    modalHidden: function () {
    },
    getNextGridPosition: function () {
      const v = this
      let newX = 0
      let newY = 0
      let lockedIn = false
      let rowCount = 0

      for (let row = 0; row < v.grid.map.length; row++) {
        for (let col = 0; col < v.grid.map[row].length; col++) {
          if (v.grid.map[row][col] === 0 && !lockedIn) {
            const diffX = v.grid.defaults.w - 1
            const diffY = v.grid.defaults.h - 1
            let anyOccupied = false

            // check surrounding grid elements
            for (let i = 0; i <= diffY; i++) {
              for (let j = 0; j <= diffX; j++) {
                if (v.grid.map.hasOwnProperty(row + i) && v.grid.map[row + i][col + j] !== 0) {
                  anyOccupied = true
                }
              }
            }

            if (!anyOccupied) {
              lockedIn = true
              newX = col
              newY = row
            }
          }
        }
        rowCount++
      }

      if (!lockedIn) {
        newX = 0
        newY = rowCount
      }

      const obj = {
        x: newX, y: newY
      }

      return obj
    },
    loadData: async function () {
      let defaultWidgets = true
      let defaultX = 0
      let defaultY = 0
      const defaultW = this.grid.defaults.w
      const defaultH = this.grid.defaults.h

      const newDisplayWidgets = []

      this.grid.map = []

      // check for all empty values, which indicates the user has received the default set of widgets
      this.usersWidgets?.forEach((value) => {
        if (value.hasOwnProperty('x') || value.hasOwnProperty('y')) {
          defaultWidgets = false
        }
      })

      if (defaultWidgets) {
        this.firstTimeUserMessaging = true
      }

      this.usersWidgets?.forEach((value, index) => {
        let newObj = { ...value } // Object.assign({}, value)
        let fluencyWidget
        let dataset
        if (value.widgetRegistryId) {
          fluencyWidget = this.widgets?.find(item => item.widgetRegistryId === value.widgetRegistryId)
        }
        if (value.reportDatasetId) {
          for (const level of Object.values(this.datasets)) {
            for (const ds of level) {
              if (ds.reportDatasetId === value.reportDatasetId) {
                dataset = ds
                break
              }
            }
          }
        }

        newObj.x = value.x || 0
        newObj.y = value.y || 0

        if (defaultWidgets) {
          newObj.x = defaultX
          newObj.y = defaultY
          newObj.w = defaultW
          newObj.h = defaultH
          this.usersWidgets[index].x = defaultX
          this.usersWidgets[index].y = defaultY
          this.usersWidgets[index].w = defaultW
          this.usersWidgets[index].h = defaultH
          this.usersWidgets[index].i = `${index}`

          defaultX++

          if (defaultX >= this.grid.numCols) {
            defaultX = 0
            defaultY++
          }
        }

        if (fluencyWidget) {
          newObj.mapping = fluencyWidget.mapping
          newObj.name = fluencyWidget.name
          newObj.i = `${index}`
          newObj.source = fluencyWidget.source
          newObj.status = fluencyWidget.status

          newObj.url = ''
          if (this.domains.hasOwnProperty(fluencyWidget.source)) {
            newObj.url = `${this.domains[fluencyWidget.source].url}${fluencyWidget.mapping}`
          }
          if (newObj.source === 'BACKPACK' && !this.dynamicComponents[newObj.name]) {
            const loadName = newObj.mapping
            this.dynamicComponents[newObj.name] = loadName.substring(1, 2).toUpperCase() + loadName.substring(2)
          }
        }

        if (dataset) {
          newObj = {
            ...dataset,
            ...newObj,
            source: 'DATA_EXPLORER',
            i: `${index}`
          }
        }

        if (fluencyWidget || dataset) {
          // add row if row doesn't exist yet
          if (!this.grid.map.hasOwnProperty(newObj.y)) {
            this.grid.map[newObj.y] = []
          }

          // if taller add additional rows
          if (newObj.h > 1) {
            const diff = newObj.h - 1

            for (let i = 0; i < diff; i++) {
              if (!this.grid.map.hasOwnProperty(newObj.y + (i + 1))) {
                this.grid.map[newObj.y + (i + 1)] = []
              }
            }
          }

          newDisplayWidgets.push(newObj)
        } else {
          console.log('ERROR: Found a non-existent widgetRegistryId', value, index)
        }
      })

      // fill array out with 0's
      for (let i = 0; i < this.grid.map.length; i++) {
        // when deleting, sometimes you can end up with empty rows. just fill them in here
        if (!this.grid.map[i]) {
          this.grid.map[i] = []
        }

        for (let j = 0; j < this.grid.numCols; j++) {
          this.grid.map[i][j] = 0
        }
      }

      newDisplayWidgets.forEach((value, index) => {
        // fill out populated spots 1's
        for (let y = value.y; y < value.y + value.h; y++) {
          for (let x = value.x; x < value.x + value.w; x++) {
            this.grid.map[y][x] = 1
          }
        }
      })

      this.displayWidgets = newDisplayWidgets
      this.dataLoaded = true
    },
    setCampaignToAnalyze (e) {
      this.$setCompat(this.campaignAnalyzer, 'campaignBeingAnalyzed', e)
    },
    openCampaignAnalyzer (item) {
      this.$setCompat(this.campaignAnalyzer, 'campaignProp', item)
      this.$setCompat(this.campaignAnalyzer, 'show', true)
    },
    hideCampaignAnalyzer () {
      this.$setCompat(this.campaignAnalyzer, 'campaignProp', undefined)
    }
  },
  async created () {
    // QUICK TABLE ACTIONS
    this.$eventBus.$on('open-analyze-campaign', this.openCampaignAnalyzer)

    this.$store.commit('loading', { dashboard: true })
    this.$eventBus.$emit('updateBreadCrumbs', [
      { name: 'Home', link: '/', icon: 'home' },
      { name: 'Dashboard', link: '/dashboard' }
    ])

    this.$store.dispatch('loadAccountDetails') // purposely leaving off await so the page loads/shows while this runs

    try {
      await this.$store.dispatch('fetchSkeleton', 'WidgetUserConfig')
    } catch (e) {
      this.$setCompat(this, 'pageLoadErrors', 'There was a problem loading Dashboard. Please try again in a moment.')
      console.log(e)
    }

    const accountPlanId = this.$route.query?.accountPlanId
    if (accountPlanId) {
      this.setName(parseInt(accountPlanId))
    }

    const [widgets, userWidgets, datasets] = await Promise.all([
      this.$res.fetch.dashboardWidgets(),
      this.$res.fetch.dashboardForUser(),
      this.$res.reporting.fetchDataSets()
    ])

    if (widgets) {
      this.$setCompat(this, 'widgets', widgets || [])
    }
    if (userWidgets) {
      const sorted = userWidgets.sort((a, b) => (a.y * a.x) - (b.y * b.x))
      this.$setCompat(this, 'usersWidgets', sorted)
    }
    if (datasets) {
      this.datasets = datasets
    }

    this.$store.commit('loading', { dashboard: false })
    this.loadData()
  },
  computed: {
    hasScrolledPastHeader () {
      return this.$store.getters.getScrolledPastHeader
    },
    headerHeight () {
      return this.$store.getters.getHeaderHeight
    },
    maxHeightInPixels () {
      const maxHeight = this.displayWidgets.reduce((prev, curr) => {
        return Math.max(prev, curr.x + curr.h)
      }, 1)
      return maxHeight * this.grid.rowHeight
    },
    dash () {
      return this.$store.getters?.user?.capabilities?.DashboardDatasets || this.$store.getters.user?.internalUser
    }
  },
  beforeUnmount () {
    this.$eventBus.$off('open-analyze-campaign', this.openCampaignAnalyzer)
  }
}
</script>

<!-- SCOPED -->
<style lang="scss" scoped>
  .backpack-layout-v1 #dashboard-wrapper {
    margin-top: -$nuxt-wrapper-top-margin;
  }

  .dashboard-container {
    background-color: #eee;

    h1, h4 {
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-weight: normal;
    }
  }

  .dashboard-container-header {
    background-color: white;
    border-bottom: 1px solid #ccc;
    margin: 0 -15px;
  }

  .widget-editing-overlay {
    z-index: 2;
    background-color: rgba(255, 255, 255, 0.85);
  }

  .widget-editing-move-icon {
    top: 50%;
    left: 50%;
    z-index: 1;
    color: #999;
    width: 200px;
    height: 200px;
    margin-left: -90px;
    margin-top: -100px;
    font-size: 200px;
  }

  .vue-js-switch {
    margin-top: 4px;
    border-radius: 11px;
    background-color: darkgray;
    font-size: 12px;
  }

  .vue-js-switch.toggled {
    background-color: $dark;
  }

  .dragger,
  .trash-widget {
    color: #676767;
  }

  .dragger:hover,
  .trash-widget:hover {
    color: $dark;
  }

  .trash-widget:hover {
    cursor: pointer;
  }

  .card.new {
    box-shadow: 0px 0 10px 5px rgba($fluency-green, 0.25);

    .card-header {
      background-color: rgba($fluency-green, 0.25);
    }
  }

  .dashboard-filter-account {
    width: 300px;
  }
  .dashboard-filter-date {
    width: 150px;
  }
</style>

<!-- GLOBAL -->
<style lang="scss">

  .vgl-item > .vgl-item__resizer {
    z-index: 5000 !important;
    background-size: 20px 20px !important;
    height: 40px !important;
    width: 40px !important;
    padding: 10px 10px 10px 10px !important;

    &:before {
      border-right-width: 4px;
      border-bottom-width: 4px;
    }
  }
  .new-widget-adder {
    background-color: #f5f5f5;
  }

  .flex-container {
    padding: 0;
    margin: 0;
    list-style: none;
    display: flex;
    font-weight: 500;
  }

  .color-over {
    font-size: .8rem;
  }

  .touch-action-none {
    touch-action: none;
  }

  .scroll-limit {
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: clip;
  }
</style>
